import { LegalShell, LegalTitle, LegalUpdated, LegalH2, LegalDivider } from './shell';

/** Public Privacy Policy — bilingual (EN + BG) on one page for Google OAuth
 *  verification (Calendar scopes). The Limited Use paragraph is required by the
 *  Google API Services User Data Policy and must be present verbatim in spirit. */
export default function PrivacyPage() {
  return (
    <LegalShell>
      {/* ---------------------------------------------------------------- EN */}
      <LegalTitle>Privacy Policy — Balance clinic ai agent</LegalTitle>
      <LegalUpdated>Last updated: 13 August 2026</LegalUpdated>
      <p>
        Balance clinic ai agent (the “Service”) is operated by Диадроу ЕООД (Diadraw Ltd.) (“we”, “us”). This
        policy explains what personal data we process and how, including data obtained through Google
        APIs. Questions: <a className="text-primary hover:underline" href="mailto:info@diadraw.com">info@diadraw.com</a>.
      </p>

      <LegalH2>1. Data we process</LegalH2>
      <ul className="list-disc space-y-1 pl-5">
        <li><b>Staff account data</b> — email, name and role, to authenticate and operate the clinic account.</li>
        <li><b>Patient / appointment data</b> — patient name, phone number, appointment date and time, and reason for visit, to book and manage appointments.</li>
        <li><b>Voice assistant data</b> — transcripts and metadata of calls handled by our AI phone assistant, to complete bookings and for support and quality.</li>
        <li><b>Google Calendar data</b> (only for specialists who choose to connect Google) — free/busy information and calendar event details (title, description, location, start/end times) from the specialist’s Google Calendar. We use it solely to compute the specialist’s availability and to create, move or cancel appointment events.</li>
      </ul>

      <LegalH2>2. How we use Google user data (Limited Use)</LegalH2>
      <p>
        Balance clinic ai agent’s use and transfer to any other app of information received from Google APIs
        will adhere to the{' '}
        <a className="text-primary hover:underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>
      <p>
        Specifically: we use Google Calendar data only to provide the appointment-scheduling features
        described above; we do not use it for advertising; we do not sell it; we do not transfer it to
        others except as necessary to provide the Service, to comply with the law, or as part of a
        merger or acquisition; and we do not allow humans to read it except with your consent, for
        security or support, or where required by law.
      </p>

      <LegalH2>3. AI voice assistant and third-party AI (data isolation)</LegalH2>
      <p>
        Our AI phone assistant is powered by <b>ElevenLabs</b> (Conversational AI), on ElevenLabs’
        <b> pay-as-you-go</b> plan. The underlying (downstream) large-language model depends on the
        language of the call: <b>Google Gemini</b> for English and <b>Qwen 3.5</b> for Bulgarian. We
        do not route Google user data through any other AI provider, model aggregator, gateway or
        model hub; there are no other downstream models.
      </p>
      <p>
        The assistant has <b>no access to Google Calendar data</b>. Our application acts strictly as
        an intermediary (a “router”) between a specialist’s calendar and the assistant: it reads only
        free/busy information from Google, computes the open appointment slots on our own servers, and
        gives the assistant nothing more than available and booked times as plain text. No Google
        Calendar API response — raw, aggregated or derived — is ever sent to the model beyond these
        computed slot times.
      </p>
      <p>
        Calendar event content — titles, descriptions, locations and attendees — is never sent to
        ElevenLabs, Gemini, Qwen or any other AI provider. The assistant is not told which real person or
        Google account a calendar belongs to; specialists, patients and time slots are referenced
        only by our own internal identifiers, never by linking a name to a specific calendar.
      </p>
      <p>
        We do not use, transfer or sell any raw, aggregated or derived Google user data to create,
        train or improve any generalized or foundational machine-learning or artificial-intelligence
        models. Per our providers’ terms, data we send is not used to train their models, and we do
        not enable any provider feature that would use this data for model training.
      </p>

      <LegalH2>4. Storage and security</LegalH2>
      <p>
        Data is stored on our servers. Google refresh tokens are encrypted at rest (AES-256-GCM).
        Access is limited to authorised personnel.
      </p>

      <LegalH2>5. Service providers</LegalH2>
      <p>
        We rely on trusted providers to run the Service: Google (Calendar), ElevenLabs (voice AI) and
        an email delivery provider. They process data only to perform their function for us.
      </p>

      <LegalH2>6. Retention</LegalH2>
      <p>
        We keep data while your account is active. When a specialist disconnects Google Calendar, the
        stored Google tokens are deleted and access is revoked.
      </p>

      <LegalH2>7. Your choices and rights</LegalH2>
      <p>
        You may access, correct or delete your data by contacting us. Specialists can disconnect Google
        Calendar at any time from within the app, and can revoke our access at{' '}
        <a className="text-primary hover:underline" href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
          myaccount.google.com/permissions
        </a>.
      </p>

      <LegalH2>8. Contact</LegalH2>
      <p>
        Диадроу ЕООД — <a className="text-primary hover:underline" href="mailto:info@diadraw.com">info@diadraw.com</a>.
      </p>

      {/* ---------------------------------------------------------------- BG */}
      <LegalDivider label="Български" />
      <LegalTitle>Политика за поверителност — Balance clinic ai agent</LegalTitle>
      <LegalUpdated>Последна актуализация: 13 август 2026 г.</LegalUpdated>
      <p>
        Balance clinic ai agent („Услугата“) се управлява от Диадроу ЕООД („ние“). Тази политика обяснява какви
        лични данни обработваме и как, включително данни, получени през Google API. Въпроси:{' '}
        <a className="text-primary hover:underline" href="mailto:info@diadraw.com">info@diadraw.com</a>.
      </p>

      <LegalH2>1. Какви данни обработваме</LegalH2>
      <ul className="list-disc space-y-1 pl-5">
        <li><b>Данни на служители</b> — имейл, име и роля, за автентикация и работа с профила на клиниката.</li>
        <li><b>Данни за пациенти / часове</b> — име и телефон на пациента, дата и час на прегледа, причина за посещение, за записване и управление на часовете.</li>
        <li><b>Данни от гласовия асистент</b> — транскрипти и метаданни на обажданията, обслужени от нашия AI телефонен асистент, за завършване на записванията и за поддръжка и качество.</li>
        <li><b>Данни от Google Calendar</b> (само за специалисти, които сами свържат Google) — информация за свободно/заето и детайли за събитията (заглавие, описание, локация, начало/край) от Google календара на специалиста. Използваме ги единствено за да изчислим свободните часове на специалиста и да създаваме, местим или отменяме събития за записани часове.</li>
      </ul>

      <LegalH2>2. Как използваме Google данните (Ограничено използване)</LegalH2>
      <p>
        Използването и прехвърлянето от Balance clinic ai agent към каквото и да е друго приложение на
        информация, получена от Google API, спазва{' '}
        <a className="text-primary hover:underline" href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
          Google API Services User Data Policy
        </a>
        , включително изискванията за Ограничено използване (Limited Use).
      </p>
      <p>
        По-конкретно: използваме данните от Google Calendar само за описаните по-горе функции по
        записване на часове; не ги използваме за реклама; не ги продаваме; не ги прехвърляме на трети
        страни освен когато е необходимо за предоставяне на Услугата, за спазване на закона или при
        сливане/придобиване; и не позволяваме на хора да ги четат, освен с ваше съгласие, за сигурност
        или поддръжка, или когато законът го изисква.
      </p>

      <LegalH2>3. AI гласов асистент и трети страни с AI (изолация на данните)</LegalH2>
      <p>
        Нашият AI телефонен асистент работи чрез <b>ElevenLabs</b> (Conversational AI), на плана
        <b> pay-as-you-go</b> на ElevenLabs. Основният (downstream) голям езиков модел зависи от езика
        на разговора: <b>Google Gemini</b> за английски и <b>Qwen 3.5</b> за български. Не прекарваме
        Google данни през друг доставчик на AI, агрегатор, gateway или model hub; няма други
        downstream модели.
      </p>
      <p>
        Асистентът <b>няма достъп до данни от Google Calendar</b>. Нашето приложение действа единствено
        като посредник („рутер“) между календара на специалиста и асистента: чете само информацията за
        свободно/заето от Google, изчислява свободните часове на наши сървъри и подава на асистента
        единствено свободните и заетите часове като обикновен текст. Никакъв отговор от Google Calendar
        API — суров, обобщен или производен — не се изпраща към модела извън тези изчислени часове.
      </p>
      <p>
        Съдържанието на събитията в календара — заглавия, описания, локации и участници — никога не се
        изпраща към ElevenLabs, Gemini, Qwen или друг доставчик на AI. На асистента не се съобщава на кой
        реален човек или Google профил принадлежи даден календар; специалистите, пациентите и часовете
        се обозначават само с наши вътрешни идентификатори, без да свързваме име с конкретен календар.
      </p>
      <p>
        Не използваме, не прехвърляме и не продаваме никакви сурови, обобщени или производни Google
        данни за създаване, обучение или подобряване на генерализирани или основни модели за машинно
        обучение или изкуствен интелект. Съгласно условията на нашите доставчици, изпращаните данни не
        се използват за обучение на моделите им, и не активираме функция на доставчик, която би
        използвала тези данни за обучение на модели.
      </p>

      <LegalH2>4. Съхранение и сигурност</LegalH2>
      <p>
        Данните се съхраняват на наши сървъри. Google refresh токените се пазят криптирани
        (AES-256-GCM). Достъпът е ограничен до оторизиран персонал.
      </p>

      <LegalH2>5. Доставчици на услуги</LegalH2>
      <p>
        Разчитаме на доверени доставчици за работата на Услугата: Google (Календар), ElevenLabs
        (гласов AI) и доставчик за изпращане на имейли. Те обработват данните само за да изпълнят
        функцията си за нас.
      </p>

      <LegalH2>6. Съхранение във времето</LegalH2>
      <p>
        Пазим данните докато профилът ви е активен. Когато специалист прекъсне връзката с Google
        Calendar, съхранените Google токени се изтриват и достъпът се отнема.
      </p>

      <LegalH2>7. Вашите права и избор</LegalH2>
      <p>
        Може да поискате достъп, корекция или изтриване на данните си, като се свържете с нас.
        Специалистите могат по всяко време да прекъснат Google Calendar от приложението и да отнемат
        достъпа ни на{' '}
        <a className="text-primary hover:underline" href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
          myaccount.google.com/permissions
        </a>.
      </p>

      <LegalH2>8. Контакт</LegalH2>
      <p>
        Диадроу ЕООД — <a className="text-primary hover:underline" href="mailto:info@diadraw.com">info@diadraw.com</a>.
      </p>
    </LegalShell>
  );
}
